/* eslint-disable */

import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import { Table, DatePicker, Select, Input, Card, Tag, message, Row, Col, Button, Tooltip, Space } from "antd";
import { PrinterOutlined, WarningOutlined } from "@ant-design/icons";
import { MCNLabel } from "../MCNLabel";
import { printBase64Pdf } from "../../utils/printUtils";
import { createRoot } from "react-dom/client";
import moment from "moment";
import { getMcnShipments, formatDateForApi, getDefaultDateRange } from "../../services/api/mcnShipmentApi";
import { getHubList } from "../../services/api/hub";
import NormalAPI from "../../api";
import urls from "../../api/urls";
import { MCN_TITLES } from "../../constants/mcnConstants";
import { DELIVERY_REPORT_STATUS_LIST } from "../../constants/shipmentStatus";
import "./McnShipmentList.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const McnShipmentList = forwardRef(({
  title,
  bookingStatuses,
  checkDestinationHubMatch,
  checkDestinationHub,
  autoFetch = false,
  currentOperationCode = []
}, ref) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
  });

  const [filters, setFilters] = useState({
    from: getDefaultDateRange().from,
    to: getDefaultDateRange().to,
    partnerCode: "",
    bookingType: "",
    search: "",
    sourceHubMatch: "",
    status: ""
  });

  const [hubList, setHubList] = useState([]);

  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const hubs = await getHubList();
        setHubList(hubs);
      } catch (error) {
        console.error("Error fetching hub list:", error);
      }
    };
    fetchHubs();
  }, []);

  // Current date range value for the DatePicker
  const currentDateRange = useMemo(() => [
    moment(filters.from, "DD-MM-YYYY"),
    moment(filters.to, "DD-MM-YYYY")
  ], [filters.from, filters.to]);

  const columns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      width: 150,
      fixed: "left",
      align: "center",
      render: (text, record) => (
        <Space>
          <strong
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              const url = `/shipment-tracker?awb=${text}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </strong>
          {title === MCN_TITLES.AT_HUB && record.tplRemarks && (
            <Tooltip title={record.tplRemarks}>
              <WarningOutlined style={{ color: "#ff4d4f" }} />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: "3PL AWB",
      dataIndex: "tplAwbNumber",
      key: "tplAwbNumber",
      width: 100,
      align: "center",
      render: (text) => text || "--"
    },
    {
      title: "Booking Complete Time",
      dataIndex: "bookingCompleteTime",
      key: "bookingCompleteTime",
      width: 180,
      align: "center",
      render: (text) => text || "--"
    },
    {
      title: "Source Hub",
      dataIndex: "sourceHubName",
      key: "sourceHubName",
      width: 120,
      align: "center",
      render: (text) => text || "--"
    },
    ...(title !== MCN_TITLES.AT_HUB
      ? [
        {
          title: "Destination Hub",
          dataIndex: "destinationHubName",
          key: "destinationHubName",
          width: 120,
          align: "center",
          render: (text) => text || "--"
        }
      ]
      : []),
    {
      title: "Status",
      dataIndex: "bookingStatus",
      key: "bookingStatus",
      width: 150,
      align: "center",
      render: (status) => <Tag color="default">{status || "--"}</Tag>
    },
    {
      title: "Partner Code",
      dataIndex: "partnerCode",
      key: "partnerCode",
      width: 120,
      align: "center",
      render: (text) => text || "--"
    },
    ...(title !== MCN_TITLES.AT_HUB && title !== MCN_TITLES.EXPECTING && title !== MCN_TITLES.DELIVERY_PENDING && title !== MCN_TITLES.DELIVERED && title !== MCN_TITLES.FAILED_MCN
      ? [
        {
          title: "Booking Type",
          dataIndex: "bookingType",
          key: "bookingType",
          width: 100,
          align: "center",
          render: (text) => text || "--"
        },
        {
          title: "Service Type",
          dataIndex: "serviceType",
          key: "serviceType",
          width: 100,
          align: "center",
          render: (text) => text || "--"
        },
        {
          title: "Type",
          dataIndex: "type",
          key: "type",
          width: 80,
          align: "center",
          render: (text) => text || "--"
        }
      ]
      : []),
    {
      title: "Status Updated On",
      dataIndex: "statusUpdatedOn",
      key: "statusUpdatedOn",
      width: 100,
      align: "center",
      render: (text) => text || "--"
    },
    ...(title === MCN_TITLES.AT_HUB || title === MCN_TITLES.FAILED_MCN
      ? [
        {
          title: "",
          dataIndex: "spacer",
          key: "spacer",
          width: 30,
          render: () => ""
        },
        {
          title: "TPL Remarks",
          dataIndex: "tplRemarks",
          key: "tplRemarks",
          width: 200,
          align: "center",
          render: (text) => text || "--"
        }]
      : []),
    ...(title === MCN_TITLES.WAITING_FOR_OUTSCAN || title === MCN_TITLES.AT_HUB || title === MCN_TITLES.FAILED_MCN
      ? [
        {
          title: "Actions",
          key: "actions",
          fixed: "right",
          width: 120,
          align: "center",
          render: (_, record) => (
            <Space size="small">
              <Tooltip
                title={record?.tplAwbNumber ? "Print MCN Label" : "3PL AWB number not available"}
                placement="top"
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={() => handleDirectPrint(record)}
                  disabled={!record?.tplAwbNumber}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px"
                  }}
                >
                  Print
                </Button>
              </Tooltip>
            </Space>
          ),
        },
      ]
      : []),
  ];

  const handleDirectPrint = async (row) => {
    try {
      // Fetch label data from API
      const response = await fetch(`${require("../../api/nodeUrls").default.MCN_LABEL_PRINT}?tplAwbNumber=${row?.tplAwbNumber}`);
      const data = await response.json();

      if (data?.labelFormat === "LABEL_DATA" && data?.data?.tplLabel) {
        printBase64Pdf(data.data.tplLabel);
        return;
      }

      const labelData = mapApiToLabel(data?.data || data); // Handle structured data inside 'data' or root

      // Create a hidden container for printing
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.position = 'fixed';
      printContainer.style.top = '0';
      printContainer.style.left = '0';
      printContainer.style.width = '100%';
      printContainer.style.height = '100%';
      printContainer.style.zIndex = '9999';
      printContainer.style.backgroundColor = 'white';
      printContainer.style.display = 'none';
      printContainer.style.bottom = '0';

      // Add print styles to remove browser headers/footers
      const printStyle = document.createElement('style');
      printStyle.id = 'print-style-override';
      printStyle.textContent = `
        @page { margin: 0; size: auto; }
        @media print {
          body { margin: 0; padding: 0; }
          html { margin: 0; padding: 0; }
        }
      `;
      document.head.appendChild(printStyle);
      document.body.appendChild(printContainer);

      // Render the label using React 18 createRoot
      const root = createRoot(printContainer);
      root.render(<MCNLabel {...labelData} />);

      // Wait for render, then print
      setTimeout(() => {
        printContainer.style.display = 'block';

        // Hide everything else
        const bodyChildren = Array.from(document.body.children);
        bodyChildren.forEach(child => {
          if (child.id !== 'print-container') {
            child.style.display = 'none';
          }
        });

        // Trigger print
        window.print();

        // Restore visibility
        bodyChildren.forEach(child => {
          if (child.id !== 'print-container') {
            child.style.display = '';
          }
        });

        // Cleanup
        root.unmount();
        document.body.removeChild(printContainer);
        const styleEl = document.getElementById('print-style-override');
        if (styleEl) {
          document.head.removeChild(styleEl);
        }
      }, 500);

    } catch (error) {
      message.error("Failed to fetch label data");
      console.error(error);
    }
  };

  const mapApiToLabel = (data) => {
    const shipmentDetails = data?.shipmentDetails || {};
    const shipTo = data?.shipTo || {};
    const shipFrom = data?.shipFrom || {};
    const items = data?.items || [];
    const barcodes = data?.barcodes || {};

    return {
      flowType: shipmentDetails?.flowType,
      orderDateTime: shipmentDetails?.orderDate,
      awbNumber: shipmentDetails?.awbNumber,
      topBarcode: barcodes?.awbNumber ? `data:image/png;base64,${barcodes.awbNumber}` : undefined,
      bottomBarcode: barcodes?.barcodeNumber ? `data:image/png;base64,${barcodes.barcodeNumber}` : undefined,
      referenceNumber: shipmentDetails?.barcodeNumber,
      mode: shipmentDetails?.mode?.toUpperCase() || "SURFACE",
      channel: shipmentDetails?.payment || "-",
      weightText: shipmentDetails?.weight,
      lbhText: shipmentDetails?.dimensions,
      shipTo: shipTo?.name,
      shipToMobile: shipTo?.mobile,
      shipToAddressLines: [shipTo?.addressLine1],
      shipToCity: shipTo?.city,
      shipToState: shipTo?.state,
      shipToPincode: shipTo?.pincode,
      routeCity: shipTo?.city,
      routePincode: shipTo?.pincode,
      shipFromTitle: `Ship From - ${shipFrom?.name || ""}`,
      shipFromMobile: shipFrom?.mobile,
      shipFromAddressLines: [
        `${shipFrom?.addressLine1 || ""}${shipFrom?.city ? `, ${shipFrom.city}` : ""}${shipFrom?.state ? `, ${shipFrom.state}` : ""}${shipFrom?.pincode ? ` - ${shipFrom.pincode}` : ""}`,
      ],
      items: (items || []).map((it, idx) => ({
        srNo: it?.srNo ?? idx + 1,
        product: it?.product,
        sku: it?.sku || "-",
        qty: it?.quantity,
        amount: it?.amount,
      })),
    };
  };

  const fetchData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      let activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
      let userId = JSON.parse(localStorage.getItem("userId") || "{}");
      let currentPremiseId = activeHub?.hubId || userId?.userInfo?.premiseId;

      const queryParams = {
        from: filters.from,
        to: filters.to,
        filterType: title === MCN_TITLES.AT_HUB ? "AT_HUB"
          : title === MCN_TITLES.EXPECTING ? "EXPECTING"
            : title === MCN_TITLES.DELIVERY_PENDING ? "DELIVERY_PENDING"
              : title === MCN_TITLES.DELIVERED ? "DELIVERED"
                : title === MCN_TITLES.WAITING_FOR_OUTSCAN ? "WAITING_FOR_OUTSCAN"
                  : title === MCN_TITLES.FAILED_MCN ? "FAILED_MCN"
                    : undefined,
        checkDestinationHub: checkDestinationHub,
        bookingStatus: filters.status || (bookingStatuses && bookingStatuses.length > 0 ? bookingStatuses.join(",") : null),
        premiseIds: filters.sourceHubMatch || null,
        bookingType: filters.bookingType || null,
        partnerCode: filters.partnerCode || null,
        search: filters.search || null,
        page: page - 1,
        size: pageSize,
      };

      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([, value]) => value != null && value !== "")
      );

      const queryString = new URLSearchParams(cleanParams).toString();

      const customHeaders = {};

      const response = await NormalAPI.Api(urls.GET_MCN_LIST + "/" + currentPremiseId + "?" + queryString)
        .getCustomHeader(customHeaders);

      if (response?.response?.status === 200) {
        setData(response.response.data.data || []);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize,
          total: response.response.data.totalElements || response.response.data.count || 0
        }));
      } else {
        message.error(response?.response?.data?.message || "Failed to fetch MCN shipments");
      }
    } catch (error) {
      message.error("Failed to fetch MCN shipments");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [autoFetch]);

  // Call API whenever filters change
  // Call API whenever filters change
  useEffect(() => {
    if (filters.from && filters.to) {
      const timeoutId = setTimeout(() => {
        fetchData(pagination.current, pagination.pageSize);
      }, 500); // Debounce API calls
      return () => clearTimeout(timeoutId);
    }
  }, [filters]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    fetchData: () => fetchData(1, pagination.pageSize),
    refreshData: () => fetchData(1, pagination.pageSize)
  }));

  const handleTableChange = (paginationInfo) => {
    fetchData(paginationInfo.current, paginationInfo.pageSize);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setFilters(prev => ({
        ...prev,
        from: formatDateForApi(dates[0].toDate()),
        to: formatDateForApi(dates[1].toDate())
      }));
    }
  };
  return (
    <div className="mcn-shipment-list">
      <Card className="filter-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Date Range</div>
            <RangePicker
              className="custom-calendar"
              onChange={handleDateRangeChange}
              format="DD-MM-YYYY"
              placeholder={["From Date", "To Date"]}
              style={{ width: "100%", height: "40px" }}
              value={currentDateRange}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">{checkDestinationHub ? "Source Hub" : "Destination Hub"}</div>
            <Select
              className="filter-select"
              style={{ width: "100%" }}
              onChange={(value) => handleFilterChange("sourceHubMatch", value)}
              placeholder={checkDestinationHub ? "Select Source Hub" : "Select Destination Hub"}
              allowClear
              showSearch
              optionFilterProp="label"
              options={hubList}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Status</div>
            <Select
              className="filter-select"
              style={{ width: "100%" }}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder="Select Status"
              allowClear
              showSearch
              optionFilterProp="children"
              options={DELIVERY_REPORT_STATUS_LIST}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Partner Code</div>
            <Select
              className="filter-select"
              style={{ width: "100%" }}
              // value={filters.partnerCode}
              onChange={(value) => handleFilterChange("partnerCode", value)}
              placeholder="Select Partner Code"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="SHIPYAARI">SHIPYAARI</Option>
              <Option value="SMILE">SMILE</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Booking Type</div>
            <Select
              className="filter-select"
              style={{ width: "100%" }}
              // value={filters.bookingType}
              onChange={(value) => handleFilterChange("bookingType", value)}
              placeholder="Select Booking Type"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value="COURIER">COURIER</Option>
              <Option value="ECOMM">ECOMM</Option>
              <Option value="CARGO">CARGO</Option>
              <Option value="INTERNATIONAL">INTERNATIONAL</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Search</div>
            <Input
              className="mr-1"
              placeholder="Search shipments..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              bordered
              style={{
                borderRadius: "5px",
                height: "40px",
              }}
              maxLength={20}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Card className="table-card">
        <Table
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          scroll={{ x: 1200 }}
          rowKey="awbNumber"
          size="middle"
        />
      </Card>
    </div>
  );
});

McnShipmentList.displayName = "McnShipmentList";

export default McnShipmentList;
